/* eslint-disable no-nested-ternary */
/**
 * Cruise API service — CRUISEGATEWAY on v2dispatch.jsp.
 *
 * Every call is a form-encoded post through the app's doPost:
 *   opid=CRUISEGATEWAY, actioncode=<action>, agentid, sessiontoken, data=<JSON>
 *
 * Every action except book answers the same envelope:
 *   { status: 'OK', statusCode: 200, message, operation, data }
 * CRUISEBOOK answers the dispatch envelope instead:
 *   { Root: { Status: 'OK', StatusCode: 200, Message, Result: '<booking ref>' } }
 *
 *   DESTINATIONS      (no data)                             -> { locationDetails: [{ destinationCode, label }] }
 *   CRUISESEARCH      { destinationCode, fromDate, toDate, adults, children,
 *                       infants, childAges, infantAges }    -> [cruise] with fareOptions
 *   CRUISEITINARY     { fareSource, bookingKey (fare) }     -> [stop]
 *   CRUISECATEGORIES  { fareSource, bookingKey (fare),
 *                       wheelchairAccessible }              -> [category]
 *   CRUISECABINS      { fareSource, bookingKey (category) } -> [cabin] with beds
 *   CRUISEPRICECHECK  { fareSource, bookingKey (cabin) }    -> { responseId, bookingKey, invoice }
 *   CRUISEBOOK        { responseId, bookingKey, ..., paxList }
 *
 * Book can debit the agent's wallet, so it is never retried and never sent
 * with an abort signal.
 */

import { doPost } from '../../../api/apiUtils';
import { getUserAgentId } from '../../../shared/helper-utils/localStorage';
import { CRUISE_ACTIONS, CRUISE_OPID } from '../constants/cruiseConstants';

const post = async (actioncode, data, extra = {}, signal = undefined) => {
  try {
    return await doPost(
      {
        opid: CRUISE_OPID,
        actioncode,
        agentid: getUserAgentId(),
        data: typeof data === 'string' ? data : JSON.stringify(data),
        ...extra,
      },
      signal,
    );
  } catch (error) {
    if (error?.name !== 'AbortError') {
      // eslint-disable-next-line no-console
      console.error(`[cruise] ${actioncode} failed:`, error);
    }
    return undefined;
  }
};

/**
 * One reader for the JSON envelope. `network: true` means no answer at all;
 * `message` is the server's own text, fit to show to the agent.
 */
export const readCruiseResult = (res) => {
  if (!res || typeof res !== 'object') {
    return { ok: false, network: true, data: null, message: '' };
  }
  const ok =
    // String(res.status).toUpperCase() === 'OK' &&
    Number(res.statusCode) === 200 && res !== null && res !== undefined;
  return {
    ok,
    network: false,
    data: ok ? (res.data ? res.data : res || null) : null,
    message: res.message || 'An error occurred',
  };
};

/**
 * Every destination the gateway sells, for the search dropdown. Called on
 * load; the request has no `data`. Resolves { ok, list, message } where
 * list is [{ destinationCode, label, fareSource, supplierName }].
 */
export const cruiseDestinationsApi = async (signal) => {
  let res;
  try {
    res = await doPost(
      {
        opid: CRUISE_OPID,
        actioncode: CRUISE_ACTIONS.DESTINATIONS,
        agentid: getUserAgentId(),
      },
      signal,
    );
  } catch (error) {
    if (error?.name !== 'AbortError') {
      // eslint-disable-next-line no-console
      console.error('[cruise] DESTINATIONS failed:', error);
    }
  }
  const read = readCruiseResult(res);
  const list = read.data?.locationDetails;
  return {
    ok: read.ok && Array.isArray(list),
    list: Array.isArray(list) ? list.filter((d) => d?.destinationCode) : [],
    message: read.message,
  };
};

export const searchCruisesApi = async (req, signal) =>
  readCruiseResult(await post(CRUISE_ACTIONS.SEARCH, req, {}, signal));

export const cruiseItineraryApi = async ({ fareSource, bookingKey }, signal) =>
  readCruiseResult(
    await post(
      CRUISE_ACTIONS.ITINERARY,
      { fareSource, bookingKey },
      {},
      signal,
    ),
  );

/** wheelchairAccessible comes from the search (default false). */
export const cruiseCategoriesApi = async (
  { fareSource, bookingKey, wheelchairAccessible = false },
  signal,
) =>
  readCruiseResult(
    await post(
      CRUISE_ACTIONS.CATEGORIES,
      {
        fareSource,
        bookingKey,
        wheelchairAccessible: Boolean(wheelchairAccessible),
      },
      {},
      signal,
    ),
  );

export const cruiseCabinsApi = async ({ fareSource, bookingKey }, signal) =>
  readCruiseResult(
    await post(CRUISE_ACTIONS.CABINS, { fareSource, bookingKey }, {}, signal),
  );

/** -> read; read.data: { status, responseId, bookingKey, invoice, ... } */
export const cruisePriceCheckApi = async ({ fareSource, bookingKey }) => {
  const read = readCruiseResult(
    await post(CRUISE_ACTIONS.PRICE_CHECK, { fareSource, bookingKey }),
  );
  const inner = read.data;
  if (
    read.ok &&
    (!inner?.responseId ||
      (inner.status && String(inner.status).toUpperCase() !== 'SUCCESS'))
  ) {
    return {
      ...read,
      ok: false,
      message: inner?.message || read.message,
    };
  }
  return read;
};

/**
 * Book. Resolves { ok, network, bookingRef, pgUrl, message }.
 * For a gateway payment the backend is expected to answer a pgurl to send
 * the agent to (same contract as bus/hotel book); for wallet it answers the
 * booking reference in Root.Result.
 *
 * No signal on purpose: a book must never be cancelled from the client.
 */
export const bookCruiseApi = async (req, { paymentmode, pgname }) => {
  const res = await post(CRUISE_ACTIONS.BOOK, req, {
    isCustomerbilling: true,
    paymentmode,
    pgname,
    uniquetimestamp: Date.now(),
  });
  if (!res || typeof res !== 'object') {
    return {
      ok: false,
      network: true,
      bookingRef: '',
      pgUrl: '',
      message: '',
    };
  }
  const root = res.Root || res;
  const result = root.Result;
  const ok =
    String(root.Status).toUpperCase() === 'OK' &&
    Number(root.StatusCode) === 200;
  const pgUrl =
    res.pgurl ||
    root.pgurl ||
    root.PgUrl ||
    (result && typeof result === 'object' ? result.pgurl || '' : '');
  const bookingRef =
    typeof result === 'string'
      ? result
      : (result && (result.bookingRef || result.BookingId)) || '';
  return {
    ok,
    network: false,
    bookingRef,
    pgUrl: ok ? pgUrl : '',
    message: root.Message || '',
  };
};
